export type Position = 'bottom-right' | 'bottom-left' | 'top-right' | 'top-left';
export type RenderMode = 'iframe' | 'shadow';

/**
 * Expanded-state presentation layout.
 * - `panel` (default): the compact corner chat panel (Phases 1-5) — a fixed
 *   400x600-ish box with an internal bounded avatar slot.
 * - `fullscreen`: full-viewport avatar takeover (Phase 6), visually matching
 *   momentum.mobeus.com's connected-chat UI — avatar video fills the whole
 *   viewport, floating message/tool-call chips overlay it, full-width input
 *   pill at the bottom. Requires `avatar: true` and `render: 'shadow'` (see
 *   bootstrap.ts's gating) — an avatar-less or iframe-mode fullscreen has
 *   nothing to take the screen over with / no way to reach a cross-document
 *   avatar mount. Only affects the EXPANDED view; the collapsed `Bubble`
 *   affordance is identical either way.
 */
export type Layout = 'panel' | 'fullscreen';

/**
 * CSS selector for a host-page element to dock `layout:"fullscreen"` into,
 * instead of taking over the whole browser viewport. When set (and the
 * fullscreen requirements below are met), the panel fills that element's
 * bounds — page content around it stays visible/interactive, matching a
 * partner-owned column (e.g. an existing avatar-mount container) rather than
 * covering their nav/content. `null` (default) keeps the original
 * full-viewport takeover. Ignored unless `layout === 'fullscreen'`.
 */
export type Dock = string | null;

/**
 * Interaction mode.
 * - `voice` (default): today's behaviour — publish the microphone on connect,
 *   play the agent's speech, show the avatar.
 * - `chat`: text in, text out. The mic is never published (so the browser
 *   never raises a permission prompt), the agent's audio is never attached,
 *   and the avatar built-in isn't installed. Deliberately independent of
 *   `avatar`/`layout`, so a partner can keep the `fullscreen` docked column
 *   and its background art and just drop the voice half of the experience.
 *
 * NOTE: this is a CLIENT-side mode. The agent still runs TTS and may still be
 * dispatched with an avatar worker; that costs money for audio nobody hears.
 * Suppressing it agent-side needs the session API to carry the mode through
 * to the worker, which is not part of this change.
 */
export type Mode = 'voice' | 'chat';

/**
 * Widget appearance mode.
 * - `light` (default): always-light palette (the historical look).
 * - `dark`: always-dark palette.
 * - `auto`: follow the host page's `prefers-color-scheme`.
 *
 * Note: distinct from the `theme` boolean below, which enables the
 * agent-driven `setTheme` site-function builtin (page theming), not the
 * widget's own colours.
 */
export type ColorScheme = 'light' | 'dark' | 'auto';

/**
 * Styling surface for the chat widget (shadow render mode).
 *
 * Three override channels, increasing in power:
 *  1. `colorScheme` — pick the built-in light/dark/auto palette.
 *  2. `vars` — override individual design tokens. Each key `k` maps to the
 *     CSS custom property `--mobeus-<k>` (e.g. `{ primary: '#0066cc' }` →
 *     `--mobeus-primary`). Applied inline on the shadow host so it beats the
 *     built-in palette.
 *  3. `css` — raw CSS injected into the shadow root for full control. Target
 *     the stable element classes (`.mobeus-panel`, `.mobeus-input`, …) or,
 *     from your own page stylesheet, the exposed `::part(...)` names.
 */
export interface EmbedStyling {
  colorScheme: ColorScheme;
  vars: Record<string, string>;
  css: string | null;
}

export type AvatarConfig =
  | false
  // `true` mounts the avatar into the panel's own internal slot (shadow
  // render mode only — see bootstrap.ts). The `{mount}` object form remains
  // for advanced/external integrations that hand-place a DOM node on the
  // host page (works in both render modes).
  | true
  | {
      mount: string;
      fit?: 'cover' | 'contain';
      opacity?: number;
    };

export interface EmbedConfig {
  position: Position;
  render: RenderMode;
  preWarm: boolean;
  /** Whether opening the collapsed panel also starts the session.
   *
   * `preWarm` only governs the automatic start on page load; opening the
   * panel has always connected on its own, which is how the "Let's talk"
   * pill has worked since day one. Partners who want the panel to open into
   * a visible, idle, not-yet-billing state with the Start button as the only
   * way in set this to `false`. Defaults to `true` so every existing embed
   * keeps today's one-click open-and-connect. */
  connectOnOpen: boolean;
  /** Opt-in typed launcher with a deferred first response (shadow fullscreen only). */
  inputStart: boolean;
  toolCalls: boolean;
  citations: boolean;
  scene: boolean;
  theme: boolean;
  avatar: AvatarConfig;
  styling: EmbedStyling;
  debug: boolean;
  /** Expanded-view presentation mode. Defaults to `panel` — every existing
   * embed keeps today's compact corner-panel behavior unless a partner
   * explicitly opts into `fullscreen` via `data-config`. See the `Layout`
   * type above for the fullscreen mode's requirements. */
  layout: Layout;
  /** See the `Dock` type above. `null` unless a partner opts in. */
  dock: Dock;
  /** See the `Mode` type above. `voice` unless a partner opts in. */
  mode: Mode;
}

export const DEFAULT_CONFIG: EmbedConfig = {
  position: 'bottom-right',
  render: 'iframe',
  preWarm: true,
  connectOnOpen: true,
  inputStart: false,
  toolCalls: true,
  citations: true,
  scene: false,
  theme: false,
  avatar: false,
  // `light` keeps the historical always-white look for existing embeds;
  // customers opt into `dark`/`auto`.
  styling: { colorScheme: 'light', vars: {}, css: null },
  debug: false,
  layout: 'panel',
  dock: null,
  mode: 'voice',
};

const POSITIONS = new Set<Position>(['bottom-right', 'bottom-left', 'top-right', 'top-left']);
const RENDERS = new Set<RenderMode>(['iframe', 'shadow']);
const COLOR_SCHEMES = new Set<ColorScheme>(['light', 'dark', 'auto']);
const LAYOUTS = new Set<Layout>(['panel', 'fullscreen']);
const MODES = new Set<Mode>(['voice', 'chat']);
// Token override keys are turned into `--mobeus-<key>` custom properties, so
// they must be safe CSS identifier fragments (letters, digits, dashes).
const VAR_KEY_RE = /^[a-zA-Z][a-zA-Z0-9-]*$/;

function parseStyling(raw: unknown): EmbedStyling {
  const styling: EmbedStyling = { colorScheme: 'light', vars: {}, css: null };
  if (!raw || typeof raw !== 'object') return styling;
  const s = raw as Record<string, unknown>;

  if (typeof s.colorScheme === 'string' && COLOR_SCHEMES.has(s.colorScheme as ColorScheme)) {
    styling.colorScheme = s.colorScheme as ColorScheme;
  }
  if (s.vars && typeof s.vars === 'object') {
    for (const [k, v] of Object.entries(s.vars as Record<string, unknown>)) {
      if (!VAR_KEY_RE.test(k)) {
        console.warn(`[Mobeus] styling.vars: ignoring invalid token key "${k}"`);
        continue;
      }
      if (typeof v === 'string' || typeof v === 'number') {
        styling.vars[k] = String(v);
      }
    }
  }
  if (typeof s.css === 'string' && s.css.trim().length > 0) {
    styling.css = s.css;
  }
  return styling;
}

export function parseConfig(raw: string | undefined | null): EmbedConfig {
  if (!raw) return { ...DEFAULT_CONFIG };

  let obj: Record<string, unknown>;
  try {
    obj = JSON.parse(raw);
    if (typeof obj !== 'object' || obj === null) {
      console.warn('[Mobeus] data-config must be a JSON object; using defaults');
      return { ...DEFAULT_CONFIG };
    }
  } catch {
    console.warn('[Mobeus] failed to parse data-config JSON; using defaults');
    return { ...DEFAULT_CONFIG };
  }

  const cfg: EmbedConfig = { ...DEFAULT_CONFIG };
  if (typeof obj.position === 'string' && POSITIONS.has(obj.position as Position)) {
    cfg.position = obj.position as Position;
  }
  if (typeof obj.render === 'string' && RENDERS.has(obj.render as RenderMode)) {
    cfg.render = obj.render as RenderMode;
  }
  if (typeof obj.layout === 'string' && LAYOUTS.has(obj.layout as Layout)) {
    cfg.layout = obj.layout as Layout;
  }
  if (typeof obj.dock === 'string' && obj.dock.trim().length > 0) {
    cfg.dock = obj.dock;
  }
  if (typeof obj.mode === 'string' && MODES.has(obj.mode as Mode)) {
    cfg.mode = obj.mode as Mode;
  }
  if (typeof obj.preWarm === 'boolean') cfg.preWarm = obj.preWarm;
  if (typeof obj.connectOnOpen === 'boolean') cfg.connectOnOpen = obj.connectOnOpen;
  if (typeof obj.inputStart === 'boolean') cfg.inputStart = obj.inputStart;
  if (typeof obj.toolCalls === 'boolean') cfg.toolCalls = obj.toolCalls;
  if (typeof obj.citations === 'boolean') cfg.citations = obj.citations;
  if (typeof obj.scene === 'boolean') cfg.scene = obj.scene;
  if (typeof obj.theme === 'boolean') cfg.theme = obj.theme;
  if (typeof obj.debug === 'boolean') cfg.debug = obj.debug;

  cfg.styling = parseStyling(obj.styling);

  if (obj.avatar === true) {
    // Internal mount: ChatPanel owns its own mount slot. Resolved against
    // render mode in bootstrap.ts (shadow-only; iframe mode warns and falls
    // back to no avatar since there's no way to reach into its document).
    cfg.avatar = true;
  } else if (obj.avatar && typeof obj.avatar === 'object') {
    const a = obj.avatar as Record<string, unknown>;
    if (typeof a.mount === 'string' && a.mount.length > 0) {
      cfg.avatar = {
        mount: a.mount,
        fit: a.fit === 'contain' ? 'contain' : 'cover',
        opacity: typeof a.opacity === 'number' ? a.opacity : 1,
      };
    } else {
      console.error('[Mobeus] avatar config missing {mount}; disabling');
      cfg.avatar = false;
    }
  }

  return cfg;
}
